// Per-product feature perks (title + description) for the generic catalog
// landing (`CatalogProductLanding`). The catalog product carries only short
// feature labels (`ServiceProduct.features`); these expand each label into the
// title + one-line description the self-serve landings show, so contact-sales
// products render the same rich feature cards. Descriptions are derived from
// each product's own `description`/`features` in `categories.ts` — keep them
// factual; refine with product/marketing copy as needed. Keyed by the product's
// stable animal codename (`ServiceProduct.name`). A product with no entry falls
// back to compact label-only cards.

export interface CatalogPerk {
  title: string;
  desc: string;
}

export const CATALOG_PERKS: Record<string, CatalogPerk[]> = {
  // Media & Streaming
  parrot: [
    { title: 'Scheduling', desc: 'Plan channel line-ups and program schedules ahead of time.' },
    { title: 'Playout', desc: '24/7 linear playout of your scheduled content.' },
    { title: 'VOD Integration', desc: 'Pull existing on-demand assets into live channels.' },
    { title: 'EPG', desc: 'Electronic program guide generated from your schedule.' },
  ],
  peacock: [
    { title: 'Multiple Formats', desc: 'Transcode source video to multiple container and codec formats.' },
    { title: 'Adaptive Bitrate', desc: 'Generate ABR renditions for smooth multi-device playback.' },
    { title: 'Thumbnails', desc: 'Auto-generate poster frames and thumbnail sprites.' },
    { title: 'Watermarking', desc: 'Overlay image or text watermarks during transcoding.' },
  ],
  pangolin: [
    { title: 'HLS AES-128', desc: 'Serve AES-128 content keys for encrypted HLS streams.' },
    { title: 'VOD + LIVE', desc: 'Protect both on-demand and live HLS delivery.' },
    { title: 'Live logs', desc: 'Real-time key-request logs for auditing.' },
    { title: 'Metrics', desc: 'Key-delivery and access metrics.' },
  ],
  // Compute
  bear: [
    { title: 'VMs', desc: 'Dedicated virtual machines with configurable CPU, RAM and disk.' },
    { title: 'Monitoring', desc: 'Built-in metrics and health monitoring for every instance.' },
    { title: 'High Availability', desc: 'HA placement keeps workloads running through failures.' },
  ],
  cheetah: [
    { title: 'Auto-scaling', desc: 'Functions scale to demand automatically, down to zero.' },
    { title: 'Pay-per-use', desc: 'Billed only for actual execution time and invocations.' },
    { title: 'API Gateway', desc: 'Expose functions over HTTP through a managed gateway.' },
  ],
  // Kubernetes
  husky: [
    { title: 'Image Storage', desc: 'Private storage for your container images.' },
    { title: 'Scanning', desc: 'Vulnerability scanning on pushed images.' },
    { title: 'Access Control', desc: 'Scoped pull/push permissions per team or token.' },
  ],
  // Databases
  elephant: [
    { title: 'RDS', desc: 'Managed MySQL, PostgreSQL and MariaDB instances.' },
    { title: 'Redis', desc: 'Fully managed Redis for caching and low-latency data.' },
    { title: 'Auto Backups', desc: 'Scheduled backups with point-in-time restore.' },
    { title: 'Monitoring', desc: 'Built-in metrics and alerting for your databases.' },
  ],
  // Observability
  heron: [
    { title: 'Uptime', desc: 'Monitor endpoint availability from multiple regions.' },
    { title: 'Latency', desc: 'Track response times and performance trends.' },
    { title: 'TLS expiry', desc: 'Watch certificate validity and warn before expiry.' },
    { title: 'Alerting', desc: 'Multi-channel alerts on incidents (email, chat, webhook).' },
  ],
  // Management
  griffin: [
    { title: 'Projects', desc: 'Group resources into isolated projects.' },
    { title: 'Teams', desc: 'Invite members and organize them into teams.' },
    { title: 'Permissions', desc: 'Fine-grained, role-based access control.' },
  ],
  unicorn: [
    { title: 'Invoices', desc: 'View and download invoices for every billing cycle.' },
    { title: 'Payments', desc: 'Manage payment methods and settle balances.' },
    { title: 'Usage', desc: 'Detailed per-service usage and cost reports.' },
  ],
  // Security
  lion: [
    { title: 'DDoS', desc: 'Absorb and mitigate volumetric DDoS attacks.' },
    { title: 'OWASP', desc: 'Managed rule sets covering the OWASP Top 10.' },
    { title: 'Bot Management', desc: 'Detect and control automated bot traffic.' },
  ],
  tiger: [
    { title: 'Auto-renewal', desc: 'Certificates renew automatically before expiry.' },
    { title: 'Wildcard', desc: 'Issue wildcard certificates for whole subdomains.' },
    { title: 'ACME', desc: 'Standard ACME issuance and validation.' },
  ],
};
